import { XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field, FilterSelect } from "~/components/filter-controls";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { WEBHOOK_SOURCES, WEBHOOK_TYPES } from "~/lib/enums";
import type { WebhookEventSearch } from "./-search";

/**
 * The filter bar over the webhook events table.
 *
 * Status is deliberately absent: it lives in the chip row above the table, where
 * the failed-first default is visible as a pressed button rather than buried in
 * a dropdown among six other filters.
 *
 * Everything here reads from the URL and writes back to it, so the table and the
 * address bar cannot disagree about what is being shown.
 */
export function WebhookEventFilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: WebhookEventSearch;
  onChange: (patch: Partial<WebhookEventSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      <Field label="Order ref">
        <DebouncedInput
          value={search.order_ref_id ?? ""}
          onCommit={(value) => onChange({ order_ref_id: value || undefined })}
          placeholder="Starts with…"
          className="w-44"
        />
      </Field>

      <Field label="Type">
        <FilterSelect
          value={search.webhook_type ?? null}
          onValueChange={(value) => onChange({ webhook_type: value ?? undefined })}
          placeholder="Any type"
          className="w-36"
          options={WEBHOOK_TYPES.map((type) => ({ value: type, label: type }))}
        />
      </Field>

      {/* Free text rather than a dropdown: event_type is whatever DurianPay put
          in the payload. The set is theirs, undocumented, and the Go handler
          deliberately does not validate it -- a new provider event must be
          searchable without shipping a release. */}
      <Field label="Event type">
        <DebouncedInput
          value={search.event_type ?? ""}
          onCommit={(value) => onChange({ event_type: value || undefined })}
          placeholder="Exact match"
          className="w-44"
        />
      </Field>

      <Field label="External ID">
        <DebouncedInput
          value={search.external_id ?? ""}
          onCommit={(value) => onChange({ external_id: value || undefined })}
          placeholder="Starts with…"
          className="w-40"
        />
      </Field>

      <Field label="Source">
        <FilterSelect
          value={search.source ?? null}
          onValueChange={(value) => onChange({ source: value ?? undefined })}
          placeholder="Any source"
          className="w-36"
          options={WEBHOOK_SOURCES.map((source) => ({ value: source, label: source }))}
        />
      </Field>

      {/* A bare YYYY-MM-DD is read as UTC and an end date is stretched to the end
          of that day, so a single-day range does include that day's events. */}
      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
